import { useEffect, useRef, useState } from 'react';
import { A, sx } from './parts';
import { useBuildLabels } from '../../ui/buildLabels';
import type { MeasureRow, MedRow, RequestRow } from './model';

/**
 * Worklist, Ask Atlas (Console V5, design wqAsk / wrAsk / wmAsk; handoff "Worklist · Ask Atlas").
 *
 * A question about the list on screen, answered only from the rows the page already shows:
 * late, due this shift, owners, a resident, what unlocks a billing code. The answer endpoint
 * (POST /assistant/worklist/ask) is not built yet, so the reading runs here over the visible
 * list and carries the design's own label, FROM THIS LIST · SAMPLE. Atlas never marks care
 * done, never logs minutes and never records a dose: a draft fills a row, a person signs.
 */

export interface AskPick {
  t: string;
  m: string;
  c: string;
}

export interface AskResult {
  ans: string;
  picks: AskPick[];
  /** A drafted reply (Requests): shown, sent only when staff press Send and acknowledge. */
  draft?: string;
  send?: () => void;
  /** A drafted value for one measure row (Measures): the row's own draft, saved by a person. */
  fill?: { rowId: string; value: string };
}

const firsts = (n: string) => String(n || '').split(/[ ,]/)[0].toLowerCase();

/** The bar itself: ASK ATLAS, the input, Ask, the suggested questions, then the answer. */
export function AskAtlasBar({
  label,
  placeholder,
  chips,
  answer,
  note,
  mb = false,
  onFill,
}: {
  label: string;
  placeholder: string;
  chips: string[];
  answer: (q: string) => AskResult;
  note: string;
  mb?: boolean;
  onFill?: (rowId: string, value: string) => void;
}) {
  // SAMPLE shows to Super Admin and Dev only (V9 no-sample.js); everyone else reads FROM THIS LIST.
  const buildLabels = useBuildLabels();
  const [q, setQ] = useState('');
  const [run, setRun] = useState('');
  // V7: the suggested questions show once the field is tapped (design wAskOpen).
  const [open, setOpen] = useState(false);
  const res = run.trim() ? answer(run.trim().toLowerCase()) : null;
  const filled = useRef<string | null>(null);
  const fill = res?.fill;
  useEffect(() => {
    if (!fill || !onFill || filled.current === run) return;
    filled.current = run;
    onFill(fill.rowId, fill.value);
  }, [fill, onFill, run]);
  const go = (text: string) => {
    setQ(text);
    setRun(text);
  };
  const picks = res ? res.picks.slice(0, 5) : [];
  return (
    <div data-screen-label={label} style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;min-width:0;' + (mb ? 'margin-bottom:8px;' : ''))}>
      <div style={sx('display:flex;gap:6px 10px;flex-wrap:wrap;align-items:center;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#1D3F6B;')}>ASK ATLAS</span>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setRun(q);
            }
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label="Ask Atlas about this list"
          style={sx('box-sizing:border-box;flex:0 1 380px;min-width:180px;height:34px;padding:0 11px;border-radius:7px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;font-family:inherit;font-size:16px;color:#22272E;')}
        />
        <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1D3F6B;" onClick={() => setRun(q)}>
          Ask
        </A>
        {open && (<div style={sx('flex:1 1 100%;display:flex;gap:4px 14px;flex-wrap:wrap;align-items:center;min-width:0;')}>
          {chips.map((c) => (
            <A key={c} style="display:inline-flex;align-items:center;min-height:28px;font-size:16px;font-weight:600;color:#264C7E;text-decoration:none;white-space:nowrap;" onClick={() => go(c)}>
              {c}
            </A>
          ))}
        </div>)}
      </div>
      {res && (
        <div aria-live="polite" style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;padding-top:8px;border-top:1px solid rgba(29,63,107,.14);')}>
          <div style={sx('display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;flex:1 1 200px;min-width:0;text-wrap:pretty;')}>{res.ans}</span>
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;')}>{buildLabels ? 'FROM THIS LIST · SAMPLE' : 'FROM THIS LIST'}</span>
            <A style="font-size:14px;font-weight:700;color:#264C7E;text-decoration:none;" onClick={() => go('')}>
              Clear
            </A>
          </div>
          {res.draft && (
            <div style={sx('display:flex;gap:8px 12px;align-items:center;flex-wrap:wrap;min-width:0;padding:8px 10px;border-radius:10px;background:#FCFAF4;border:1px dashed rgba(29,63,107,.35);')}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;color:#1D3F6B;')}>DRAFT</span>
              <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;color:#22272E;')}>{res.draft}</span>
              <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1F6B52;white-space:nowrap;" onClick={() => res.send?.()}>
                Send and acknowledge
              </A>
            </div>
          )}
          {picks.map((r, i) => (
            <div key={i} style={sx('display:flex;gap:4px 12px;flex-wrap:wrap;align-items:baseline;min-width:0;font-size:16px;')}>
              <span style={sx('flex:1 1 240px;min-width:0;color:#22272E;')}>{r.t}</span>
              <span style={sx(`color:${r.c};font-weight:600;`)}>{r.m}</span>
            </div>
          ))}
          <span style={sx('font-size:14px;color:#54606C;')}>{res.picks.length > 5 ? `+${res.picks.length - 5} more in the list below` : ''}</span>
          <span style={sx('font-size:14px;color:#54606C;')}>{note}</span>
        </div>
      )}
    </div>
  );
}

// ── Measures (design L47400-47433) ──────────────────────────────────────────

export function measuresAsk(rows: MeasureRow[], ro: boolean) {
  const open = rows.filter((r) => !r.isDone);
  const names = [...new Set(rows.map((r) => r.res))];
  // The answer quotes the minute, not the ticking second the row itself shows.
  const pick = (r: MeasureRow): AskPick => ({ t: r.res + ' · ' + r.task, m: (r.dueText || '').replace(/ \d+s/, '') + ' · ' + r.owner, c: r.dueColor });
  const answer = (aq: string): AskResult => {
    const resHit = names.find((n) => aq.indexOf(n.split(' ')[0].toLowerCase()) >= 0);
    const numHit = aq.match(/(\d+(?:\.\d+)?)/);
    let picks: MeasureRow[] = [];
    if (resHit && numHit && /weigh|weight|lbs|pound/.test(aq)) {
      const it = open.find((x) => x.res === resHit && x.cap === 'number');
      if (!it) return { ans: 'No open weigh-in for ' + resHit + ' on this list.', picks: [] };
      if (ro) return { ans: 'Read only here. Floor staff can say this to fill the row.', picks: [] };
      return { ans: 'Drafted ' + numHit[1] + ' ' + it.ph + ' into ' + resHit + '’s row. Check it and press Done to sign.', picks: [pick(it)], fill: { rowId: it.id, value: numHit[1] } };
    }
    if (resHit) {
      picks = open.filter((r) => r.res === resHit);
      return { ans: picks.length + ' open for ' + resHit + '.', picks: picks.map(pick) };
    }
    if (/late|overdue|behind/.test(aq)) {
      picks = open.filter((r) => r.ms < 0);
      return { ans: picks.length ? picks.length + ' late right now.' : 'Nothing is late.', picks: picks.map(pick) };
    }
    if (/nobody|unowned|unassigned|no one|who has/.test(aq)) {
      picks = open.filter((r) => r.owner === 'Nobody owns this');
      return { ans: picks.length + ' with nobody on them.', picks: picks.map(pick) };
    }
    if (/money|block|bill|\$|paid/.test(aq)) {
      picks = open.filter((r) => r.track === 'DRIVE' && r.badge);
      return { ans: picks.length + ' open rows unlock a billing code. Soonest first.', picks: picks.map(pick) };
    }
    if (/today|shift|before|due/.test(aq)) {
      picks = open.filter((r) => r.ms >= 0 && r.ms < 12 * 3_600_000);
      return { ans: picks.length + ' due before the end of this shift.', picks: picks.map(pick) };
    }
    if (/mine|my |yours/.test(aq)) {
      picks = open.filter((r) => r.owner === 'Yours');
      return { ans: picks.length + ' on you.', picks: picks.map(pick) };
    }
    return { ans: 'I can only answer from this list: late, due, owners, residents, or what unlocks money.', picks: [] };
  };
  const first = (open[0]?.res ?? names[0] ?? '').split(' ')[0];
  const weigh = open.find((r) => r.cap === 'number');
  const chips = ['What’s late?', 'Who has nobody on it?']
    .concat(first ? ['What’s left for ' + first + '?'] : [])
    .concat(['What’s blocking money?'])
    .concat(!ro && weigh ? [weigh.res.split(' ')[0] + ' weighed 142'] : []);
  return { answer, chips };
}

// ── Requests (design L43149-43183) ──────────────────────────────────────────

export function requestsAsk(all: RequestRow[], ro: boolean, ack: (id: string) => void) {
  const rows = all.filter((q) => q.isOpen);
  const rq = (q: RequestRow): AskPick => ({ t: q.name + ' · ' + q.kind + ' ' + q.quote, m: q.clock + ' · ' + q.st, c: q.isUrg ? '#B3261E' : '#3C454F' });
  const answer = (a: string): AskResult => {
    const hit = rows.filter((q) => a.indexOf(firsts(q.name)) >= 0);
    if (/^(tell|reply|text|let)\b/.test(a)) {
      if (!hit.length) return { ans: 'No open request from that resident.', picks: [] };
      const q = hit[0];
      const msg = (a.match(/(?:tell|let) \S+(?: know)?(?: that)? (.+)$/) || [])[1] || 'someone is on the way';
      if (ro) return { ans: 'Read only here. Floor staff can draft replies.', picks: hit.map(rq) };
      return {
        ans: 'Draft reply to ' + q.name + '. Nothing is sent until you press Send.',
        picks: [rq(q)],
        draft: '“' + msg.charAt(0).toUpperCase() + msg.slice(1) + '.”',
        send: () => {
          if (q.showAck) ack(q.id);
        },
      };
    }
    if (hit.length) return { ans: hit.length + ' open from ' + hit[0].name + '.', picks: hit.map(rq) };
    if (/urgent|now|emergenc/.test(a)) {
      const r = rows.filter((q) => q.isUrg).sort((x, y) => x.since - y.since);
      return { ans: r.length ? r.length + ' urgent, longest wait first.' : 'Nothing urgent is open.', picks: r.map(rq) };
    }
    if (/long|wait|oldest/.test(a)) {
      const r = rows.slice().sort((x, y) => x.since - y.since);
      return { ans: r.length ? r[0].name + ' has waited longest.' : 'Nobody is waiting.', picks: r.map(rq) };
    }
    if (/family/.test(a)) {
      const r = rows.filter((q) => q.src === 'Family concierge');
      return { ans: r.length + ' from family.', picks: r.map(rq) };
    }
    if (/ack|nobody|unanswered|not seen/.test(a)) {
      const r = rows.filter((q) => q.st === 'OPEN');
      return { ans: r.length + ' nobody has acknowledged yet.', picks: r.map(rq) };
    }
    return { ans: 'I can only answer from these requests: urgent, longest wait, family, not acknowledged, or a resident’s name.', picks: [] };
  };
  const who = rows.map((q) => q.name);
  const chips = ['What’s urgent?', 'Who’s waited longest?', 'Not acknowledged yet?', 'From family?'].concat(!ro && who[0] ? ['Tell ' + who[0].split(' ')[0] + ' I’m on my way'] : []);
  return { answer, chips };
}

// ── Med pass (design L43184-43195): questions only, never a dose record ──────

export function medsAsk(meds: MedRow[]) {
  const md = (m: MedRow): AskPick => ({ t: m.res + ' · ' + m.name + ' ' + m.dose, m: m.recorded ? m.st || 'recorded' : m.dueText.replace(/ \d+s/, ''), c: !m.recorded && m.dueColor === '#B3261E' ? '#B3261E' : '#3C454F' });
  const answer = (a: string): AskResult => {
    if (/\b(gave|given|give|took|taken|administer|record|log|mark)\b/.test(a)) return { ans: 'Atlas doesn’t record doses. Tap Given or Missed on the dose itself; that tap is the nurse’s signature.', picks: [] };
    const open = meds.filter((m) => !m.recorded);
    const hit = meds.filter((m) => a.indexOf(firsts(m.res)) >= 0);
    if (hit.length) {
      const r = hit.filter((m) => !m.recorded);
      return { ans: r.length + ' still to give for ' + hit[0].res + '.', picks: (r.length ? r : hit).map(md) };
    }
    if (/late|overdue|behind/.test(a)) {
      const r = open.filter((m) => m.dueColor === '#B3261E');
      return { ans: r.length ? r.length + ' doses are late.' : 'No dose is late.', picks: r.map(md) };
    }
    if (/miss|refus/.test(a)) {
      const r = meds.filter((m) => m.st === 'MISSED' || m.st === 'REFUSED');
      return { ans: r.length ? r.length + ' missed or refused this pass.' : 'None missed or refused this pass.', picks: r.map(md) };
    }
    if (/next|hour|due|left|still/.test(a)) {
      const r = open.filter((m) => m.dueColor !== '#B3261E');
      return { ans: r.length + ' still to give, soonest first.', picks: r.map(md) };
    }
    return { ans: 'I can only answer from this med pass: late, still to give, missed or refused, or a resident’s name.', picks: [] };
  };
  const chips = ['What’s late?', 'What’s due next?', 'Missed or refused?'].concat(meds[0] ? ['What’s left for ' + meds[0].res.split(' ')[0] + '?'] : []);
  return { answer, chips };
}
